/*
 * As the basket duck is a huge duck that have quite a lot of selectors we create a separate file
 * for these selectors.
 */
import { createSelector } from 'reselect';

import { MODIFICATION_MODES } from '../../constants/basket';
import * as PRESTATIONS from '../../constants/prestations';
import { PREVIOUS_PRO_CHOICES, PRO_CHOICE_SOURCES } from '../../constants/pro-accounts';
import { UNIVERSES } from '../../constants/universes';
import { DEFAULT_CHILDCARE_BASKET_PRICES } from '../../prices/childcare-prices';
import { DEFAULT_CLEANING_BASKET_PRICES } from '../../prices/cleaning-prices';
import { categoryPrice, computePrices, getCategoryPrestationsPrices } from '../../prices/prices';
import { DEFAULT_WELLNESS_BASKET_PRICES } from '../../prices/wellness-prices';
import type {
  BasketContentItem,
  Category,
  Config,
  Prestation,
  PrestationOrCategoryPrice,
  PrestationReference,
  Subcategory,
  Universe,
} from '../../types/commons';
import {
  buildBasketPrimaryAppointment,
  buildBasketSecondaryAppointments,
  buildPrestationGroups,
  extractAppointmentFromBooking,
  getBasketContentDuration,
  getChildcareAllowanceAmount as getChildcareAllowanceAmountUtil,
  getFirstPrestationFromContent,
  getTotalPrestationsCount,
} from '../../utils/basket';
import moment from '../../utils/moment';
import { isWanted } from '../../utils/previous-pros';
import { isHourlyBased } from '../../utils/universes';
import type { CustomerRootState } from './types';

const getUniverses = (state: CustomerRootState) => state.universes.all;

export const getAppointmentDate = (state: CustomerRootState) => state.basket.appointmentDate;
export const getUniverseRef = (state: CustomerRootState) => state.basket.universe;
export const getBookingUuid = (state: CustomerRootState) => state.basket.bookingUuid;
export const getBasketCustomer = (state: CustomerRootState) =>
  'first_name' in state.basket.customer ? state.basket.customer : undefined;
export const getBasketAddress = (state: CustomerRootState) => state.basket.address;
export const getBasketDuration = (state: CustomerRootState) => state.basket.duration;
export const getBasketWeeklyRecurrence = (state: CustomerRootState) => state.basket.weekly_recurrence;
export const getBasketContent = (state: CustomerRootState) => state.basket.basketContent;
export const getCleaningPrestation = (state: CustomerRootState) => state.basket.cleaningPrestation;
export const getCleaningPrestationOptions = (state: CustomerRootState) => state.basket.cleaningPrestationOptions;
const getBasketAppointment = (state: CustomerRootState) => state.basket.appointment;
export const getPrimaryAppointment = (state: CustomerRootState) => state.basket.appointment.primary;
export const getZone = (state: CustomerRootState) => state.basket.zone;
export const getPromotion = (state: CustomerRootState) => state.basket.promotion;
export const getGiftCard = (state: CustomerRootState) => state.basket.giftCard;
const getMinPriceSurchargeAccepted = (state: CustomerRootState) => state.basket.minPriceSurchargeAccepted;
export const getProChoices = (state: CustomerRootState) => state.basket.proChoices;
export const getMode = (state: CustomerRootState) => state.basket.mode;
export const getPotentialCreation = (state: CustomerRootState) => state.basket.potentialCreation;
const getPotentialUpdate = (state: CustomerRootState) => state.basket.potentialUpdate;
export const getPreviousBooking = (state: CustomerRootState) => state.basket.bookingToModify.current;
export const getChildrenParams = (state: CustomerRootState) => state.basket.childrenParams;
export const getChildcareAllowanceParams = (state: CustomerRootState) => state.basket.childcareAllowanceParams;
export const getChildcareHoursPerWeek = (state: CustomerRootState) => state.basket.childcareHoursPerWeek;
const getSchedules = (state: CustomerRootState) => state.basket.schedules;
const getIncludeHolidays = (state: CustomerRootState) => state.basket.includeHolidays;
const getMinAppointment = (state: CustomerRootState) => state.basket.closedTimeSlots.minAppointment;
const getBookingToModify = (state: CustomerRootState) => state.basket.bookingToModify;
export const getIsMembershipRequested = (state: CustomerRootState) => state.basket.isMembershipRequested;
export const getIsInstantTaxCreditSelected = (state: CustomerRootState) => state.basket.isInstantTaxCreditSelected;
export const getUpdateAllOccurrences = (state: CustomerRootState) => state.basket.updateAllOccurrences;
export const getHasPets = (state: CustomerRootState) => state.basket.hasPets;
export const getIsBookingForSelf = (state: CustomerRootState) => state.basket.isBookingForSelf;
export const getHighlightedTimeSlots = (state: CustomerRootState) => state.basket.highlightedTimeSlots.all;

export const getCurrentUniverse = createSelector(
  [getUniverses, getUniverseRef, getBookingToModify, getMode],
  (universes, universeRef, bookingToModify, mode) => {
    // This if can only be true for customer-related code because only the customer basket state uses bookingToModify.
    // The pro app currently also uses this selector and they also use
    // the modes modifyPrestations and modifyAppointment, but the goal is to no longer use this customer selector
    // in the pro app and to use react-query for retrieving the universe.
    if (mode && MODIFICATION_MODES.includes(mode) && bookingToModify?.universeAtDate) {
      return bookingToModify.universeAtDate;
    }
    return universes.find((u) => u.reference === universeRef);
  },
);

export const getModifiedBooking = createSelector([getBookingToModify], (bookingToModify) =>
  'public_id' in bookingToModify.current ? bookingToModify.current : undefined,
);

export const getMinimumAppointment = createSelector(
  [getCurrentUniverse, getMinAppointment],
  (universe, minAppointment) => minAppointment || moment(universe?.minimum_appointment),
);

export const getBonusPrestationsRef = createSelector([getUniverses], (universes) =>
  universes
    .reduce((acc: Category[], universe: Universe) => acc.concat(universe.categories), [])
    .reduce((acc: Subcategory[], category: Category) => acc.concat(category.subcategories), [])
    .reduce((acc: Prestation[], subCategory: Subcategory) => acc.concat(subCategory.prestations), [])
    .filter((prestation) => prestation.bonus)
    .map((prestation) => prestation.reference),
);

export const getBasketCategoriesPrices = createSelector(
  [
    getCurrentUniverse,
    getBasketContent,
    getZone,
    (_state: CustomerRootState, config: Config, hasOrWantsMembership: boolean) => ({ config, hasOrWantsMembership }),
  ],
  (universe, basketContent, zone, { config, hasOrWantsMembership }): number[] => {
    if (universe && config) {
      return basketContent.map((category: BasketContentItem) =>
        categoryPrice({
          basketCategory: category,
          universe,
          zone,
          comboPrices: config.prestations_combo_prices,
          membershipComboPrices: config.prestations_member_combo_prices,
          shouldUseMembershipPrice: hasOrWantsMembership,
        }),
      );
    }
    return [];
  },
);

export const getPrestationPricesForBasketCategories = createSelector(
  [getCurrentUniverse, getBasketContent, getZone, (_state: CustomerRootState, config: Config) => config],
  (universe, basketContent, zone, config): Record<PrestationReference, PrestationOrCategoryPrice>[] => {
    if (universe && config) {
      return basketContent.map((basketCategory: BasketContentItem) =>
        getCategoryPrestationsPrices({
          basketCategory,
          universe,
          zone,
          comboPrices: config.prestations_combo_prices,
          membershipComboPrices: config.prestations_member_combo_prices,
        }),
      );
    }
    return [];
  },
);

export const getAllValidProChoices = createSelector([getProChoices], (proChoices) =>
  proChoices.some((choice) => choice.value === PREVIOUS_PRO_CHOICES.ONLY)
    ? proChoices.filter((choice) => choice.value === PREVIOUS_PRO_CHOICES.ONLY)
    : proChoices.filter((choice) => choice.value),
);

export const getAllProChoices = createSelector([getProChoices], (proChoices) =>
  proChoices.filter((choice) => choice.value),
);

export const getWantedProChoices = createSelector([getProChoices], (proChoices) => proChoices.filter(isWanted));

export const hasProProfileChoice = createSelector([getProChoices], (proChoices) =>
  proChoices.some((choice) => choice.source === PRO_CHOICE_SOURCES.PRO_PROFILE),
);

export const getCreatedOrUpdatedPotential = createSelector(
  [getMode, getPotentialCreation, getPotentialUpdate],
  (mode, potentialCreation, potentialUpdate) =>
    mode === 'creation' ? potentialCreation.result : potentialUpdate.result,
);

export const getPreviousBookingAppointment = createSelector([getPreviousBooking], (booking) =>
  'appointment' in booking && booking.appointment ? extractAppointmentFromBooking(booking) : null,
);

export const getPreviousBookingOrNull = createSelector([getPreviousBooking], (booking) =>
  'type' in booking ? booking : null,
);

export const getFirstPrestation = createSelector([getBasketContent], getFirstPrestationFromContent);

export const getPrestationCount = createSelector([getBasketContent], getTotalPrestationsCount);

export const getAllPrestationsInBasket = createSelector(
  [getBasketContent, getCleaningPrestation, getCleaningPrestationOptions],
  (basketContent, cleaningPrestation, cleaningPrestationOptions) => {
    if (cleaningPrestation) {
      return [cleaningPrestation.reference, ...(cleaningPrestationOptions || []).map((option) => option.reference)];
    }
    return basketContent.reduce(
      (allPrestations: PrestationReference[], category: BasketContentItem) =>
        allPrestations.concat(category.prestations),
      [],
    );
  },
);

export const getPrestationDuration = createSelector(
  [getBasketContent, getCurrentUniverse, getBasketDuration],
  (basketContent, universe, basketDuration) =>
    universe && isHourlyBased(universe) ? basketDuration || 0 : getBasketContentDuration(basketContent, universe),
);

export const isChildcareSpot = createSelector(
  [getBasketContent],
  (basketContent) =>
    getFirstPrestationFromContent(basketContent) === PRESTATIONS.BABYSITTING_DAY ||
    getFirstPrestationFromContent(basketContent) === PRESTATIONS.BABYSITTING_NIGHT,
);

export const getChildcareAllowanceAmount = createSelector(
  [getCurrentUniverse, getBasketContent, getChildcareAllowanceParams, getChildrenParams],
  (universe, basketContent, childcareAllowanceParams, childrenParams) =>
    getChildcareAllowanceAmountUtil({
      universe,
      basketContent,
      childcareAllowanceParams,
      childrenParams,
    }),
);

export const getBasketPrices = createSelector(
  [
    getCurrentUniverse,
    getPrimaryAppointment,
    getPromotion,
    getGiftCard,
    getMinPriceSurchargeAccepted,
    getBasketContent,
    getCleaningPrestation,
    getCleaningPrestationOptions,
    getBasketDuration,
    getZone,
    getSchedules,
    getChildcareHoursPerWeek,
    getIncludeHolidays,
    getChildcareAllowanceAmount,
    getChildrenParams,
    (_state: CustomerRootState, config: Config) => config,
  ],
  (
    universe,
    primaryAppointment,
    promotion,
    giftCard,
    minPriceSurchargeAccepted,
    basketContent,
    cleaningPrestation,
    cleaningPrestationOptions,
    duration,
    zone,
    schedules,
    childcareHoursPerWeek,
    includeHolidays,
    allowanceAmount,
    childrenParams,
    config,
  ) => {
    if (universe && zone && config) {
      return computePrices({
        universe,
        primaryAppointment,
        basketContent,
        cleaningPrestation,
        cleaningPrestationOptions,
        promotion,
        giftCard: 'code' in giftCard ? giftCard : undefined,
        minPriceSurchargeAccepted,
        duration,
        zone,
        schedules,
        childcareHoursPerWeek,
        includeHolidays,
        allowanceAmount,
        youngestChildAge: childrenParams.youngestChildAge,
        comboPrices: config.prestations_combo_prices,
        membershipComboPrices: config.prestations_member_combo_prices,
      });
    }
    /*
     * Here I return the correct kind in the basket with default prices so when we check which basket to show, we show
     * the correct one.
     */
    switch (universe?.reference) {
      case UNIVERSES.CHILDCARE:
        return DEFAULT_CHILDCARE_BASKET_PRICES;
      case UNIVERSES.CLEANING:
        return DEFAULT_CLEANING_BASKET_PRICES;
      default:
        return DEFAULT_WELLNESS_BASKET_PRICES;
    }
  },
);

export const getBasketPrimaryAppointmentFormatted = createSelector([getBasketAppointment], (appointment) =>
  buildBasketPrimaryAppointment({ appointment }),
);

export const getBasketSecondaryAppointmentsFormatted = createSelector([getBasketAppointment], (appointment) =>
  buildBasketSecondaryAppointments({ appointment }),
);

export const getBasketPrestationGroupsFormatted = createSelector(
  [getBasketContent, getCleaningPrestation, getCleaningPrestationOptions],
  (basketContent, cleaningPrestation, cleaningPrestationOptions) =>
    buildPrestationGroups({ basketContent, cleaningPrestation, cleaningPrestationOptions }),
);
